<template>
  <div>
    <u-error-handling
      v-if="errorObject"
      :error-object="errorObject"
    />
    <v-alert
      :value="missingVariables.length > 0"
      class="mb-3"
      type="warning"
    >
      Folgende Variablen fehlen und müssen zwingend erstellt werden:<br>
      <br>
      <ul>
        <li
          v-for="(missingVariable, missingVariableIndex) in missingVariables"
          :key="missingVariableIndex"
        >
          {{ missingVariable.name }}
        </li>
      </ul>
      <button
        class="btn btn-success"
        placement="bottom"
        title="Variable bearbeiten"
        type="button"
        @click="createMissingVariables"
      >
        Variablen erstellen
      </button>
    </v-alert>
    <u-loader v-if="isLoading" />
    <div
      v-else
      class="variableWrapper"
    >
      <table class="w-100 variablesTable">
        <thead>
          <tr>
            <th>Name</th>
            <th>Typ</th>
            <th>Wert</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(variable, variableIndex) in filteredVariables"
            :key="variableIndex"
          >
            <td>
              <div v-if="variable.id != variableToEditId">
                {{ variable.name }}
              </div>
              <div v-else>
                <v-text-field
                  v-model="variable.name"
                  class="mr-4"
                  label="Name"
                />
              </div>
            </td>
            <td>
              <div v-if="variable.id != variableToEditId">
                {{
                  variableTypeLabels[variable.type]
                    ? variableTypeLabels[variable.type]
                    : variableTypeLabels[0]
                }}
              </div>
              <div v-else>
                <v-radio-group v-model="variable.type">
                  <v-radio
                    v-for="(type, index) in variableTypes"
                    :key="index"
                    :label="type.name"
                    :value="type.value"
                    :disabled="variable.type == 5"
                    color="black"
                  />
                </v-radio-group>
              </div>
            </td>
            <td>
              <div v-if="variable.id != variableToEditId">
                <div v-if="variable.type == 4">
                  {{ variable.value }}
                  <span v-if="variable.value == 'Y'">Ja</span>
                  <span v-if="variable.value == 'N'">Nein</span>
                  <span v-else>Individuell</span>
                </div>
                <div v-else-if="variable.type == 1 || variable.type == 3">
                  {{ variable.value }}
                </div>
              </div>
              <div v-else>
                <v-radio-group
                  v-if="variable.type == 4"
                  v-model="variable.value"
                >
                  <v-radio
                    v-for="(type, index) in [
                      { name: 'Ja', value: 'Y' },
                      { name: 'Nein', value: 'N' },
                      { name: 'Individuell', value: '' },
                    ]"
                    :key="index"
                    :label="type.name"
                    :value="type.value"
                    color="black"
                  />
                </v-radio-group>
                <v-text-field
                  v-else-if="variable.type == 1"
                  v-model="variable.value"
                  class="mr-4"
                  label="Wert"
                  type="number"
                />
                <v-text-field
                  v-else-if="variable.type == 3"
                  v-model="variable.value"
                  class="mr-4"
                  label="Wert"
                />
              </div>
            </td>
            <td>
              <div v-if="variable.id != variableToEditId">
                <button
                  v-if="canUserEdit"
                  v-b-tooltip.hover
                  class="btn btn-primary smallActionButton"
                  placement="bottom"
                  title="Variable bearbeiten"
                  type="button"
                  @click="variableToEditId = variable.id"
                >
                  <i class="fas fa-edit" />
                </button>
                <button
                  v-if="isNotARequiredVariable(variable.name) && canUserEdit"
                  v-b-modal.deleteVariable
                  v-b-tooltip.hover
                  class="btn btn-danger smallActionButton mr-1 ml-1"
                  placement="bottom"
                  title="Variable löschen"
                  type="button"
                  @click="
                    variableToDelete = variable.id;
                    deleteErrorObject = null;
                  "
                >
                  <i class="fas fa-trash-alt" />
                </button>
              </div>
              <div v-else>
                <button
                  v-b-tooltip.hover
                  class="btn btn-success smallActionButton"
                  placement="bottom"
                  title="Speichern"
                  type="button"
                  @click="saveVariable(variable)"
                >
                  <i class="fas fa-check" />
                </button>
                <button
                  v-b-tooltip.hover
                  class="btn btn-danger smallActionButton mr-1 ml-1"
                  placement="bottom"
                  title="Abbrechen"
                  type="button"
                  @click="
                    variableToEditId = -1;
                    allVariables = JSON.parse(
                      JSON.stringify(originalVariables),
                    );
                  "
                >
                  <i class="fas fa-times" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <button
        v-if="
          allVariables.filter((variable) => {
            return variable.id == 0;
          }).length == 0 && canUserEdit
        "
        id="addNewTextBlockButton"
        v-b-tooltip.hover
        class="btn btn-success float-right mr-4 mt-4"
        placement="bottom"
        title="Neue Variable hinzufügen"
        type="button"
        @click="addNewVariable()"
      >
        <i class="fas fa-plus" />
      </button>
    </div>
    <b-modal
      id="deleteVariable"
      ref="deleteVariable"
      hide-footer
      no-fade
      title="Textblock löschen"
    >
      <div class="d-block mb-4">
        <u-error-handling
          v-if="deleteErrorObject"
          :error-object="deleteErrorObject"
        />
        Sind Sie sicher, dass Sie diese Variable löschen möchten?
      </div>
      <b-button
        variant="success"
        @click="deleteVariable()"
      >
        Ok
      </b-button>
      <b-button
        class="ml-2"
        variant="danger"
        @click="hideModal('deleteVariable')"
      >
        Abbrechen
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import {
  DeleteVariable,
  SaveVariable,
  CreateNewVariable,
} from "@/services/minimum.wage.services/variables.minimum.wages.js";
import { MinimumWageMixin } from "@/components/minimum.wage/mixins/MinimumWageMixin";

const VUEX_IDENTIFIER = "Variables";

export default {
  mixins: [MinimumWageMixin],
  data() {
    return {
      deleteErrorObject: null,
      allVariables: [],
      filteredVariables: [],
      originalVariables: [],
      variableToEditId: -1,
      variableToDelete: -1,
      requiredVariables: [
        { name: "AnteilFeiertagsentschädigung", type: 2 },
        { name: "Anteil13Monatslohn", type: 2 },
        { name: "AnzahlFeiertage", type: 2 },
        { name: "AnzahlFerientage", type: 2 },
        { name: "AnteilFerienentschädigung", type: 2 },
        {
          name: "Bruttostundenlohn",
          type: 3,
          value:
            "Basisstundenlohn + Ferienentschädigung + Feiertagsentschädigung + Entschädigung13Monatslohn",
        },
        {
          name: "Entschädigung13Monatslohn",
          type: 3,
          value:
            "(Basisstundenlohn + Ferienentschädigung + Feiertagsentschädigung) * Anteil13Monatslohn",
        },
        {
          name: "Ferienentschädigung",
          type: 3,
          value:
            "AnteilFerienentschädigung * (Basisstundenlohn + Feiertagsentschädigung)",
        },
        {
          name: "Feiertagsentschädigung",
          type: 3,
          value: "AnteilFeiertagsentschädigung * Basisstundenlohn",
        },
        { name: "Basisstundenlohn", type: 2 },
        { name: "Monatslohn", type: 2 },
      ],
      missingVariables: [],
      variableTypeLabels: [
        "nicht definiert",
        "Fixwert",
        "Berechnet",
        "Formel",
        "Flag (Ja/Nein)",
        "Feiertagskalender",
      ],
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters.isVariablesLoading;
    },
    errorObject() {
      return this.$store.state.errorObjects.Variables;
    },
    variableTypes() {
      let types = [
        { value: 1, name: this.variableTypeLabels[1] },
        { value: 4, name: this.variableTypeLabels[4] },
        { value: 2, name: this.variableTypeLabels[2] },
        { value: 3, name: this.variableTypeLabels[3] },
      ];

      if (
        this.$store.state.mindestLohnKonfiguration.Configuration
          .useGlobalerFeiertagskalender
      ) {
        let variableToEdit = this.filteredVariables.find(
          (variable) => variable.id == this.variableToEditId,
        );
        if (variableToEdit && variableToEdit.name == "AnzahlFeiertage") {
          types.push({ value: 5, name: "Feiertagskalender" });
        }
      }
      return types;
    },
  },
  created() {
    let that = this;

    this.$store.watch(
      (state, getters) => getters.variables,
      (newValue) => {
        that.allVariables = JSON.parse(JSON.stringify(newValue));
        that.filteredVariables = that.allVariables; //.sort((a,b) => (a.type > b.type) ? 1 : -1);
        that.originalVariables = JSON.parse(JSON.stringify(newValue));

        that.checkForRequiredVariables();
      },
    );

    this.getAllVariables();
  },
  methods: {
    hideModal(modalId) {
      this.$refs[modalId].hide();
    },
    isNotARequiredVariable(variableName) {
      var found = this.requiredVariables.find(
        (variable) => variable.name === variableName,
      );
      return found == null;
    },
    checkForRequiredVariables() {
      this.missingVariables = [];

      this.requiredVariables.map((reqVariable) => {
        var found = this.filteredVariables.findIndex((filterVariable) => {
          return filterVariable.name === reqVariable.name;
        });

        if (found == -1) {
          this.missingVariables.push(reqVariable);
        }
      });
    },
    getAllVariables() {
      this.$store.dispatch("getVariables", {
        contractId: this.contractId,
        versionId: this.versionId,
        editionId: this.editionId,
      });
    },
    createMissingVariables() {
      this.missingVariablesToCreate = [...this.missingVariables];
      this.startLoading(VUEX_IDENTIFIER);
      this.createNextMissingVariable();
    },
    createNextMissingVariable() {
      let that = this;

      var variable = this.missingVariablesToCreate.pop();

      var newVariableObject = {
        id: 0,
        configurationId: that.editionId,
        name: variable.name,
        value: variable.value,
        type: variable.type,
      };

      CreateNewVariable(
        that.contractId,
        that.versionId,
        that.editionId,
        newVariableObject,
        () => {
          if (that.missingVariablesToCreate.length > 0) {
            that.createNextMissingVariable();
          } else {
            that.getAllVariables();
            that.stopLoading(VUEX_IDENTIFIER, null);
          }
        },
        (error) => {
          that.stopLoading(VUEX_IDENTIFIER, error);
        },
      );
    },
    addNewVariable() {
      var newVariableObject = {
        id: 0,
        configurationId: 0,
        name: "",
        value: "",
        type: 1,
      };

      this.allVariables.push(newVariableObject);
      this.filteredVariables = this.allVariables.sort((a, b) =>
        a.type > b.type ? 1 : -1,
      );
      this.checkForRequiredVariables();
      this.variableToEditId = 0;
    },
    deleteVariable() {
      this.startLoading(VUEX_IDENTIFIER);

      DeleteVariable(
        this.contractId,
        this.versionId,
        this.editionId,
        this.variableToDelete,
        () => {
          this.getAllVariables();
          this.hideModal("deleteVariable");
        },
        (error) => {
          this.stopLoading(VUEX_IDENTIFIER);
          this.deleteErrorObject = error;
        },
      );
    },
    saveVariable(variableToSave) {
      this.startLoading(VUEX_IDENTIFIER);

      if (variableToSave.id != 0) {
        SaveVariable(
          this.contractId,
          this.versionId,
          this.editionId,
          variableToSave,
          () => {
            this.variableToEditId = -1;
            this.getAllVariables();
          },
          (error) => {
            this.stopLoading(VUEX_IDENTIFIER, error);
          },
        );
      } else {
        CreateNewVariable(
          this.contractId,
          this.versionId,
          this.editionId,
          variableToSave,
          () => {
            this.getAllVariables();
            this.isLoading = false;
          },
          (error) => {
            this.stopLoading(VUEX_IDENTIFIER, error);
          },
        );
      }
    },
  },
};
</script>

<style>
.variableWrapper .v-label.theme--light {
  color: black;
  margin-bottom: 0px;
  font-size: 14px;
}

.variableWrapper .v-input--selection-controls__input {
  margin-right: 2px;
}

.variableWrapper .theme--light.v-icon {
  font-size: 22px;
}

.variableWrapper .v-input--selection-controls {
  margin-top: 0px;
  padding-top: 0px;
}
</style>

<style scoped>
.smallActionButton {
  width: 30px;
  height: 30px;
  font-size: 17px;
  padding: 0px;
}

.variablesTable {
  font-size: small;
  table-layout: fixed;
}

.variablesTable thead {
  font-size: 15px;
  border-bottom: 1px solid black;
}

.variablesTable thead th {
  padding-bottom: 1em;
}

.variablesTable tbody tr {
  border-bottom: 1px solid #ddd;
}

.variablesTable th {
  padding-right: 30px;
}

.variablesTable thead th:nth-child(1) {
  width: 25%;
}

.variablesTable thead th:nth-child(2) {
  width: 15%;
}

.variablesTable thead th:nth-child(3) {
  width: 50%;
}

.variablesTable thead th:nth-child(4) {
  width: 10%;
}

.variablesTable tr td {
  padding-bottom: 10px;
  padding-top: 10px;
  vertical-align: top;
}

.variablesTable tr:nth-child(2n) td {
  padding-top: 10px;
}
</style>
